/**
 * Planogram (FIXTURE_TASK) notification transformer.
 * Converts gateway notification models into UI display types.
 */

import { FixtureTaskMetadata } from '@amzn/coral_com-amazon-jwostoremanagementnotification-coral/FixtureTaskMetadata'
import { FixtureTaskAggregatedMetadata } from '@amzn/coral_com-amazon-jwostoremanagementnotification-coral/FixtureTaskAggregatedMetadata'
import { ReactiveScanTaskStats } from '@amzn/coral_com-amazon-jwostoremanagementnotification-coral/ReactiveScanTaskStats'
import type { GetNotificationsOutput } from '@amzn/coral_com-amazon-jwostoremanagementgateway/GetNotificationsOutput'
import { ImageResource } from '@amzn/coral_com-amazon-ihmvirgo-coral/ImageResource'
import { PosedCamera } from '@amzn/ihm-shared-video-player/dist/scene/objects/posed-camera'
import { IAggregatedNotification, IIndividualNotification } from '@src/pages/NotificationsPage/types'
import {
    PLANOGRAM_TOOL_NAME,
    PLANOGRAM_TOOL_URL,
    PLANOGRAM_DEEP_URL_PREFIX,
    PLANOGRAM_AGGREGATED_MESSAGE,
    planogramIndividualMessage,
    planogramAggregatedDetail,
    ACTION_SOLVE,
    ACTION_VIEW_DETAILS,
} from './notificationMessages'

/**
 * Converts "row4.rack5.fv" → "Row 4 Rack 5", "row1.dispenser10.fv" → "Row 1 Dispenser 10"
 */
export function formatFixtureAddress (fixtureId: string): string {
    const parts = fixtureId.replace(/\.fv$/, '').split('.')
    return parts
        .map((part) => {
            const match = part.match(/^([a-zA-Z]+)(\d+)$/)
            if (!match) return part
            return match[1].charAt(0).toUpperCase() + match[1].slice(1) + ' ' + match[2]
        })
        .join(' ')
}

export function getToolName (notificationType: string): string {
    switch (notificationType) {
    case 'FIXTURE_TASK': return PLANOGRAM_TOOL_NAME
    default: return notificationType
    }
}

export function transformAggregatedNotification (
    apiAgg: GetNotificationsOutput['summary']['aggregatedNotifications'][number],
): IAggregatedNotification {
    let message = ''

    const metadata = apiAgg.aggregatedMetadata
    if (metadata instanceof FixtureTaskAggregatedMetadata) {
        const reactiveScanStats = metadata.statsByType.REACTIVE_SCAN
        if (reactiveScanStats && reactiveScanStats instanceof ReactiveScanTaskStats) {
            message = planogramAggregatedDetail(reactiveScanStats.totalFixtures, reactiveScanStats.totalProductSpaces)
        }
    }

    return {
        notificationMessage: PLANOGRAM_AGGREGATED_MESSAGE,
        numPendingTasks: apiAgg.totalNotifications,
        message,
        mostRecentNotificationTimestamp: apiAgg.createdAt,
        toolUrl: PLANOGRAM_TOOL_URL,
    }
}

/**
 * Computes clockwise rotation degrees from an ImageResource's camera pose data.
 * Mirrors the logic in FixtureListImage from IhmVirtualStoreToolReact.
 * Returns 0 if rotation cannot be determined.
 */
function computeImageRotation (imgResource: ImageResource, imageSize: number): number {
    try {
        const posedCamera = new PosedCamera(
            imgResource.camera,
            imgResource.cameraSpaceToWorldSpacePose,
        )
        const cropOrigin = imgResource.cropBoundingBoxOrigin
        const frameWidth = imgResource.camera.calibrationFrameWidth
        const frameHeight = imgResource.camera.calibrationFrameHeight
        posedCamera.setViewOffset(frameWidth, frameHeight, cropOrigin.x, cropOrigin.y, imageSize, imageSize)

        if (posedCamera.frameRotation > 0) {
            return 360 - posedCamera.frameRotation
        }
    } catch (error) {
        console.error('Failed to compute image rotation:', error)
    }
    return 0
}

const DEFAULT_IMAGE_SIZE = 80

export function transformIndividualNotification (
    apiNotif: GetNotificationsOutput['notifications'][number],
    isCurrentStore: boolean,
    imageDataMap?: Map<string, ImageResource>,
): IIndividualNotification {
    const metadata = apiNotif.metadata as FixtureTaskMetadata
    const imgResource = imageDataMap?.get(metadata.fixtureId)

    return {
        imageUrl: imgResource?.imageUrl ?? null,
        imageRotation: imgResource ? computeImageRotation(imgResource, DEFAULT_IMAGE_SIZE) : 0,
        notificationMessage: planogramIndividualMessage(metadata.productSpaceCount),
        supplementaryMessage: undefined,
        fixtureAddress: formatFixtureAddress(metadata.fixtureId),
        timestamp: apiNotif.createdAt,
        notificationAction: isCurrentStore ? ACTION_SOLVE : ACTION_VIEW_DETAILS,
        deepUrl: `${PLANOGRAM_DEEP_URL_PREFIX}${metadata.fixtureId}`,
    }
}
